import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Button,
  Pagination,
  Modal,
  Box,
  CircularProgress,
  Grid,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  deleteMeasurement,
  fetchQuotationSuggestion,
  listMeasurementsByStatus,
} from "../../redux/actions/measurementActions";

const QuotationList = ({
  title = "Quotation List",
  limit = 10,
  showPagination = true,
  showSearch = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State variables
  const [tag, setTag] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [inputValue, setInputValue] = useState("");

  // Redux selectors
  const { measurements, totalPages, loading } = useSelector(
    (state) => state.quotationList
  );

  const { success: deleteSuccess } = useSelector(
    (state) => state.measurementDelete
  );

  const { suggestions } = useSelector((state) => state.quotationSuggestions);

  useEffect(() => {
    dispatch(listMeasurementsByStatus(tag, page, limit));
  }, [dispatch, tag, page, limit, deleteSuccess]);

  useEffect(() => {
    dispatch(fetchQuotationSuggestion());
  }, [dispatch]);

  const handleRowClick = (id) => {
    navigate(`/order/submit-quotation/${id}`);
  };

  const handleSearchChange = (event, newValue) => {
    if (newValue) {
      setTag(newValue.tag);
      setPage(1);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    setTag(newInputValue);
    setPage(1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setTag(inputValue);
      setPage(1);
    }
  };

  // Handle pagination changes
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle delete icon click
  const handleDeleteClick = (id) => {
    setSelectedQuotationId(id);
    setShowModal(true);
  };

  // Confirm deletion
  const handleConfirmDelete = () => {
    dispatch(deleteMeasurement(selectedQuotationId));
    setShowModal(false);
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    setShowModal(false);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        style={{
          marginTop: "1rem",
          padding: "1rem",
          paddingLeft: "2rem",
          paddingRight: "2rem",
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Grid item>
            <Typography
              variant="h6"
              component="div"
              style={{ marginBottom: "1rem" }}
            >
              {title}
            </Typography>
          </Grid>
          {showSearch && (
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={suggestions}
                getOptionLabel={(option) => option.tag || ""}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                    }}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography
                        variant="subtitle1"
                        component="div"
                        style={{ fontWeight: "bold" }}
                      >
                        {option.tag}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.clientName && <div>{option.clientName}</div>}
                        {option.email && <div>{option.email}</div>}
                        {option.address && <div>{option.address}</div>}
                      </Typography>
                    </Box>
                  </li>
                )}
                onChange={handleSearchChange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Tag, client name or client email"
                    variant="outlined"
                    fullWidth
                    onKeyDown={handleKeyDown}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ height: "200px" }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : measurements?.length === 0 ? (
          <Typography>No quotations found.</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>Client Email</TableCell>
                <TableCell>Client Phone</TableCell>
                <TableCell>Client Address</TableCell>
                <TableCell>Measurements Count</TableCell>
                <TableCell>Total Sqft</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Quoted Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {measurements?.map((quotation) => (
                <TableRow
                  key={quotation._id}
                  hover
                  onClick={() => handleRowClick(quotation._id)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>
                    {new Date(quotation.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{quotation.clientName || "N/A"}</TableCell>
                  <TableCell>{quotation.clientEmail || "N/A"}</TableCell>
                  <TableCell>{quotation.clientPhone || "N/A"}</TableCell>
                  <TableCell>{quotation.clientAddress || "N/A"}</TableCell>
                  <TableCell>{quotation.measurements?.length || 0}</TableCell>
                  <TableCell>{quotation.totalSqft || "N/A"}</TableCell>
                  <TableCell>{quotation.tag || "N/A"}</TableCell>
                  <TableCell>{quotation.quotedPrice || "N/A"}</TableCell>
                  <TableCell>
                    {" "}
                    <Chip
                      label={quotation.orderStatus}
                      sx={{
                        backgroundColor: "#FFD700",
                        color: "black",
                      }}
                    />{" "}
                  </TableCell>
                  <TableCell>
                    <i
                      className="bi bi-trash-fill text-danger"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(quotation._id);
                      }}
                    ></i>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {showPagination && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            style={{ marginTop: "1rem" }}
          />
        )}
      </TableContainer>

      {/* Confirmation Modal */}
      <Modal
        open={showModal}
        onClose={handleCancelDelete}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="modal-title" variant="h6" component="h2">
            Confirm Delete
          </Typography>
          <Typography id="modal-description" sx={{ mt: 2 }}>
            Are you sure you want to delete this quotation?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <Button onClick={handleCancelDelete} sx={{ marginRight: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default QuotationList;
